export const ADMIN_ROLES = {
  ADMIN_EVENT: 'AdminEvent',
  ADMIN_ACCOMMODATION: 'AdminAccomodation',
  ADMIN_CONTACT: 'AdminContact',
  ADMIN_PROMOTION: 'AdminPromotion',
};

/**
 * Normalizes role string to canonical key or null for role-restricted admins.
 * Returns 'AdminEvent', 'AdminAccomodation', 'AdminContact', 'AdminPromotion', or null for standard admins.
 */
export function getRoleKey(role) {
  if (!role) return null;
  const clean = String(role).toLowerCase().replace(/[^a-z0-9]/g, '');
  if (clean === 'adminevent' || clean === 'eventadmin') {
    return ADMIN_ROLES.ADMIN_EVENT;
  }
  if (
    clean === 'adminaccommodation' ||
    clean === 'adminaccomodation' ||
    clean === 'accommodationadmin' ||
    clean === 'accomodationadmin'
  ) {
    return ADMIN_ROLES.ADMIN_ACCOMMODATION;
  }
  if (clean === 'admincontact' || clean === 'contactadmin') {
    return ADMIN_ROLES.ADMIN_CONTACT;
  }
  if (clean === 'adminpromotion' || clean === 'promotionadmin') {
    return ADMIN_ROLES.ADMIN_PROMOTION;
  }
  return null;
}

/**
 * Checks if user is Super Admin.
 */
export function isSuperAdminRole(role) {
  if (!role) return false;
  return /super\s*admin/i.test(role) || String(role).toLowerCase().replace(/[^a-z0-9]/g, '') === 'superadmin';
}

/**
 * Checks if user is standard Admin.
 */
export function isAdminRole(role) {
  if (!role) return false;
  const clean = String(role).toLowerCase().replace(/[^a-z0-9]/g, '');
  return clean === 'admin' || clean === 'adminuser';
}

/**
 * Checks if user is strict Admin User ('adminuser').
 */
export function isStrictAdminUser(role) {
  if (!role) return false;
  const clean = String(role).toLowerCase().replace(/[^a-z0-9]/g, '');
  return clean === 'adminuser';
}

/**
 * Returns allowed paths array for the specific admin role.
 * If user is standard Admin or Super Admin, returns null (meaning full access to all screens).
 */
export function getAllowedPathsForRole(role) {
  const roleKey = getRoleKey(role);
  if (roleKey === ADMIN_ROLES.ADMIN_EVENT) {
    return ['/events', '/event-types', '/notifications'];
  }
  if (roleKey === ADMIN_ROLES.ADMIN_ACCOMMODATION) {
    return ['/accommodation', '/accommodation-facilities', '/accommodation-types', '/notifications'];
  }
  if (roleKey === ADMIN_ROLES.ADMIN_CONTACT) {
    return ['/contacts', '/categories', '/service-sub-categories', '/contact-inquiries', '/notifications'];
  }
  if (roleKey === ADMIN_ROLES.ADMIN_PROMOTION) {
    return ['/promotions', '/promotion-types', '/notifications'];
  }
  return null;
}

/**
 * Returns the default home route for a user based on their role.
 */
export function getDefaultRouteForRole(role) {
  if (isStrictAdminUser(role)) {
    return '/users';
  }
  const roleKey = getRoleKey(role);
  if (roleKey === ADMIN_ROLES.ADMIN_EVENT) {
    return '/events';
  }
  if (roleKey === ADMIN_ROLES.ADMIN_ACCOMMODATION) {
    return '/accommodation';
  }
  if (roleKey === ADMIN_ROLES.ADMIN_CONTACT) {
    return '/contacts';
  }
  if (roleKey === ADMIN_ROLES.ADMIN_PROMOTION) {
    return '/promotions';
  }
  return '/dashboard';
}

/**
 * Checks if a given path is accessible by the user's role.
 */
export function isPathAllowedForRole(role, path) {
  if (isStrictAdminUser(role) && path === '/dashboard') {
    return false;
  }
  const allowed = getAllowedPathsForRole(role);
  if (!allowed) return true;
  return allowed.includes(path);
}

/**
 * Returns human-friendly role display label.
 */
export function formatRoleDisplay(role) {
  if (!role) return '';
  const roleKey = getRoleKey(role);
  if (roleKey === ADMIN_ROLES.ADMIN_EVENT) return 'Admin Event';
  if (roleKey === ADMIN_ROLES.ADMIN_ACCOMMODATION) return 'Admin Accommodation';
  if (roleKey === ADMIN_ROLES.ADMIN_CONTACT) return 'Admin Contact';
  if (roleKey === ADMIN_ROLES.ADMIN_PROMOTION) return 'Admin Promotion';

  const r = String(role).toLowerCase().replace(/\s+/g, '');
  if (r === 'adminuser') return 'Admin User';
  if (r === 'admin') return 'Admin';
  if (r === 'customer') return 'Customer';
  if (r === 'superadmin') return 'Super Admin';
  return role;
}

/**
 * Checks if the role has management permissions (add, edit, update status) for Events screens.
 */
export function canManageEvents(role) {
  if (!role) return false;
  if (isSuperAdminRole(role) || isAdminRole(role)) return true;
  return getRoleKey(role) === ADMIN_ROLES.ADMIN_EVENT;
}

/**
 * Checks if the role has management permissions (add, edit, update status) for Accommodation screens.
 */
export function canManageAccommodation(role) {
  if (!role) return false;
  if (isSuperAdminRole(role) || isAdminRole(role)) return true;
  return getRoleKey(role) === ADMIN_ROLES.ADMIN_ACCOMMODATION;
}

/**
 * Checks if the role has management permissions (add, edit, update status) for Contacts screens.
 */
export function canManageContacts(role) {
  if (!role) return false;
  if (isSuperAdminRole(role) || isAdminRole(role)) return true;
  return getRoleKey(role) === ADMIN_ROLES.ADMIN_CONTACT;
}

/**
 * Checks if the role has management permissions (add, edit, update status) for Promotions screens.
 */
export function canManagePromotions(role) {
  if (!role) return false;
  if (isSuperAdminRole(role) || isAdminRole(role)) return true;
  return getRoleKey(role) === ADMIN_ROLES.ADMIN_PROMOTION;
}

/**
 * Checks if the role can DELETE items in a specific domain.
 * Super Admin: YES.
 * Domain Admin (AdminEvent, AdminAccomodation, AdminContact, AdminPromotion): YES for their domain.
 * Standard Admin: NO (can add/edit/update status, but NOT delete).
 */
export function canDeleteInDomain(role, domain) {
  if (!role) return false;
  if (isSuperAdminRole(role)) return true;
  const roleKey = getRoleKey(role);
  if (domain === 'events' && roleKey === ADMIN_ROLES.ADMIN_EVENT) return true;
  if (domain === 'accommodation' && roleKey === ADMIN_ROLES.ADMIN_ACCOMMODATION) return true;
  if (domain === 'contacts' && roleKey === ADMIN_ROLES.ADMIN_CONTACT) return true;
  if (domain === 'promotions' && roleKey === ADMIN_ROLES.ADMIN_PROMOTION) return true;
  return false;
}

export function canDeleteEvents(role) {
  return canDeleteInDomain(role, 'events');
}

export function canDeleteAccommodation(role) {
  return canDeleteInDomain(role, 'accommodation');
}

export function canDeleteContacts(role) {
  return canDeleteInDomain(role, 'contacts');
}

export function canDeletePromotions(role) {
  return canDeleteInDomain(role, 'promotions');
}
